import { ModalWindowService } from '@anecdotes/design-system/modals';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { Observable, firstValueFrom, shareReplay } from 'rxjs';
import { PluginService } from '@anecdotes/data-store';
import { TrustCenterFacadeService, SubprocessorResponse, OrderResourceType, TrustCenterEventService, TrustResourceEventDataProperty } from '@anecdotes/shared-trust-center';
import { getCurrentlyVisibleObjects } from '../../../../utils';
import { SubprocessorEditSidePanelComponent } from '../subprocessor-edit-side-panel/subprocessor-edit-side-panel.component';
import { TrustCenterModalService } from '../../../../services/trustcenter-modals/trustcenter-modals.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-subprocessors',
  templateUrl: './subprocessors.component.html',
  styleUrls: ['./subprocessors.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubprocessorsComponent implements OnInit {
  editing$: Observable<boolean>;
  isPreviewMode$: Observable<boolean>;
  grantedAccess$: Observable<boolean>;
  subprocessors: SubprocessorResponse[];

  constructor(
    private modalWindowService: ModalWindowService,
    private pluginService: PluginService,
    private trustCenterFacadeService: TrustCenterFacadeService,
    private trustCenterModalService: TrustCenterModalService,
    private destroyRef: DestroyRef,
    private cd: ChangeDetectorRef,
    private readonly trustCenterEventService: TrustCenterEventService,
  ) {}

  ngOnInit(): void {
    this.editing$ = this.trustCenterFacadeService.isEditing();
    this.isPreviewMode$ = this.trustCenterFacadeService.isPreviewMode().pipe(shareReplay({ bufferSize: 1, refCount: true }));

    getCurrentlyVisibleObjects(this.editing$, this.trustCenterFacadeService.getSubprocessors(), 'position')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((subprocessors) => {
        this.subprocessors = subprocessors;
        this.cd.detectChanges();
      });
    this.grantedAccess$ = this.trustCenterFacadeService.isLoggedIn();
  }

  changeOrder(event: CdkDragDrop<SubprocessorResponse[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.subprocessors, event.previousIndex, event.currentIndex);

      const orderList = this.subprocessors.map((subprocessor) => subprocessor.id);
      this.trustCenterFacadeService.reorderResources(OrderResourceType.Subprocessor, orderList);
    }
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.${key}`;
  }

  getServiceIcon(serviceId: string): string {
    return this.pluginService.getServiceIconLinkSync(serviceId);
  }

  openCustomSubprocessorSidepanel(subprocessor?: SubprocessorResponse): void {
    this.trustCenterModalService.openSubprocessorCreateSidePanel(subprocessor);
  }

  async openSubprocessorSidepanel(subprocessor: SubprocessorResponse): Promise<void> {
    const editing = await firstValueFrom(this.editing$);
    const isPreviewMode = await firstValueFrom(this.isPreviewMode$);
    if (!editing) {
      return;
    }

    this.modalWindowService.close();

    if (!isPreviewMode) {
      const companyName = await firstValueFrom(this.trustCenterFacadeService.getName());
      this.trustCenterEventService.trackClickOnResourceItem(TrustResourceEventDataProperty.Subprocessors, subprocessor.id, companyName);
    }

    if (subprocessor.is_custom) {
      this.openCustomSubprocessorSidepanel(subprocessor);
    } else {
      this.modalWindowService.openMediumSlidePanel({
        componentsToSwitch: [
          {
            id: 'app-subprocessor-edit-side-panel',
            componentType: SubprocessorEditSidePanelComponent,
            contextData: {
              subprocessor,
            },
          },
        ],
        options: {
          closeOnEsc: true,
        },
      });
    }
  }
}
